import {useMemo} from 'react'
import {useB2BAgencyPacks} from './useB2BAgencyPacks.ts'
import {useInvitationCodesByAgency} from './useInvitationCodes.ts'

// Cumulative pooled activity across all of an agency's packs — shared by the
// "Estadísticas" and "Packs y TripBooks" tabs so both read the exact same
// numbers regardless of which tab currently renders them.
export interface AgencyPackActivity {
    totalUnits: number
    printedN: number
    createdN: number
    deliveredN: number
    createdPct: number
    deliveredPct: number
}

export interface AgencyGenericCode {
    id: string
    code: string
    isPaid: boolean
}

export function useAgencyPackActivity(agencyId: string, genericCode?: string, isSelfService = false, enabled = true) {
    const {packs, packages, isLoading, packsError} = useB2BAgencyPacks(agencyId, isSelfService, enabled)
    const {invitationCodes} = useInvitationCodesByAgency(agencyId)

    // Reusable agency-wide codes (unlimitedRedemption) — a free one and/or a paid
    // one (stripeProductCode) — belong in the invitation-link banner, never
    // counted as pending rows in "Códigos prerellenados".
    const genericCodes: AgencyGenericCode[] = useMemo(() => {
        const fromRecords = invitationCodes
            .filter((record): record is typeof record & {id: string; code: string} => record.unlimitedRedemption === true && !!record.code)
            .map((record) => ({id: record.id, code: record.code, isPaid: !!record.stripeProductCode}))
        if (fromRecords.length > 0) return fromRecords
        return genericCode ? [{id: 'legacy-generic-code', code: genericCode, isPaid: false}] : []
    }, [invitationCodes, genericCode])

    const genericCodeIds = useMemo(
        () => new Set(invitationCodes.filter((record) => record.unlimitedRedemption === true).map((record) => record.id)),
        [invitationCodes]
    )

    // All packs pooled into one set of codes — packs are a purchase unit, not a
    // grouping the agency needs to navigate by.
    const printed = useMemo(() => packs.flatMap((p) => p.printed), [packs])
    const created = useMemo(() => packs.flatMap((p) => p.created), [packs])
    const inactive = useMemo(() => packs.flatMap((p) => p.inactive), [packs])
    const visibleInactive = useMemo(
        () => genericCodeIds.size ? inactive.filter((c) => !genericCodeIds.has(c.codeId)) : inactive,
        [inactive, genericCodeIds]
    )

    const activity: AgencyPackActivity = useMemo(() => {
        const totalUnits = packs.reduce((acc, p) => acc + p.pack.units, 0)
        const pctOf = (n: number) => totalUnits ? Math.round((n / totalUnits) * 100) : 0
        // "creados" counts everyone who has at least a TripBook, printed or not.
        const printedN = printed.length
        const createdN = created.length + printedN
        const deliveredN = createdN + visibleInactive.filter((c) => c.status === 'DELIVERED').length
        return {
            totalUnits, printedN, createdN, deliveredN,
            createdPct: pctOf(createdN),
            deliveredPct: pctOf(deliveredN),
        }
    }, [packs, printed, created, visibleInactive])

    return {
        packs,
        // Raw package list (units + pagination per pack) — the assign dialog's
        // "Paginación del TripBook" options come from here.
        packages,
        isLoading,
        packsError,
        isPayPerUse: packs.length === 0,
        printed, created, inactive, visibleInactive,
        genericCodes, genericCodeIds,
        activity,
    }
}
